import type { LookPiece } from "./look-records.ts";
import type { Looks } from "./looks.ts";
import type { DialogueCheckAttribute } from "../npcs/dialogue-marks.ts";
import type { Attribute } from "./attributes.ts";

/**
 * The rules tables' shapes (ADR-069 decisions 2 and 3): what a record of each table holds once the validator
 * has checked it, the closed sets the code knows, and what a load hands its readers. Every number is a whole
 * number: durations in ticks, lengths in sixteenths of a tile, angles in whole degrees, amounts in whole points
 * or whole percents.
 */

/**
 * The twelve tables, in the order the validator reads and reports them: ADR-069 decision 1's six, `crits`, which
 * ADR-073 decision 16 adds, `items`, which ADR-091 decision 1 adds, `looks`, which ADR-094 decision 1 adds,
 * `creature_levels` and `xp_gap`, which ADR-082's amendment of 2026-10-15 adds, and `backgrounds`, which ADR-136's
 * amendment of 2026-10-28 (decision 6) adds.
 */
export const RULES_TABLES = ["abilities", "backgrounds", "constants", "creature_levels", "crits", "effects", "items", "levels", "looks", "resources", "weapons", "xp_gap"] as const;
export type RulesTableName = (typeof RULES_TABLES)[number];

/**
 * The backgrounds of Bible 4.2, the ids the `backgrounds` table holds each once.
 */
export const BACKGROUND_IDS = ["lyceum_applicant", "street_survivor", "guild_apprentice", "hedge_practitioner", "fallen_noble", "wanderer"] as const;
export type BackgroundId = (typeof BACKGROUND_IDS)[number];

/**
 * The most a background adds to a dialogue check.
 */
export const MAX_BACKGROUND_CHECK_BONUS = 10;

/**
 * The points a background adds to a character's attributes in all (ADR-138 decision 4): 2 to one and 1 to another.
 */
export const BACKGROUND_STARTING_POINTS = 3;

/**
 * A background's part in dialogue checks (ADR-136's amendment of 2026-10-28, decision 6): the one attribute, of the
 * seven of Bible 4.1, whose checks it adds `checkBonus` to, or null for none; and the points it adds to the stored
 * attributes when it is set (ADR-138 decision 4), one or two attributes summing to `BACKGROUND_STARTING_POINTS`.
 */
export interface Background {
  readonly id: BackgroundId;
  readonly checkAttribute: DialogueCheckAttribute | null;
  readonly checkBonus: number;
  readonly startingAttributes: Readonly<Partial<Record<Attribute, number>>>;
}

/**
 * The three resources, the ids the `resources` table holds each once and a cost or a pool names.
 */
export const RESOURCE_IDS = ["health", "stamina", "focus"] as const;
export type ResourceId = (typeof RESOURCE_IDS)[number];

/**
 * The damage types an ability or a periodic effect deals (the spec's 3.3, and `poison` from ADR-079's amendment of
 * 2026-10-02); nothing mitigates them by type until armour and resistances exist. A `hit` event's flags carry the type
 * in two bits, so `poison` is the last type the layout holds.
 */
export const DAMAGE_TYPES = ["physical", "thermal", "kinetic", "poison"] as const;
export type DamageType = (typeof DAMAGE_TYPES)[number];

/**
 * The shapes the game resolves (Bible 13.4): a cone from the caster along the aim, a line along the aim, the selected
 * target, oneself or a selected ally (ADR-079's amendment of 2026-10-03), or oneself alone, the shape an item's use runs
 * (ADR-091 decision 2), and a projectile, a shot that flies in a straight line to a landing point and bursts there in a
 * circle (ADR-095 decision 2). Bible 13.4's ring joins when an ability uses it.
 */
export const SHAPE_KINDS = ["cone", "line", "target", "ally", "self", "projectile"] as const;
export type ShapeKind = (typeof SHAPE_KINDS)[number];

/**
 * A weapon's blows: `basic`, a left click toward the pointer, and `heavy`, the heavy blow.
 */
export const BLOWS = ["basic", "heavy"] as const;
export type Blow = (typeof BLOWS)[number];

/**
 * What a regeneration amount counts: whole points, or whole percents of the pool.
 */
export const REGENERATION_UNITS = ["points", "percent"] as const;
export type RegenerationUnit = (typeof REGENERATION_UNITS)[number];

/**
 * The constants the code reads, each a whole number of ticks unless it is a count: the in-combat window after the last hostile action
 * given or taken (question 7), the time from a death to its respawn (question 8), and how long PvP damage dealt keeps
 * a character Combatant (Bible 6.1, ADR-073 decision 13 and ADR-076's amendment of 2026-10-01: a blow taken never
 * starts or restarts it), and how long the flag toggle's switch counts
 * toward Flag on and toward Protected (Bible 6.1, ADR-076 decision 2), and the diminishing returns' window after the
 * last control landed (Bible 5.5, ADR-079 decision 5 and its amendment of 2026-10-07: no immunity follows), and how long a character whose
 * connection ends in a fight stays in the world (the linger, the Phase 2 spec's question 9, ADR-084 decision 9), and how
 * long a character whose player chose to leave stays (`logout_stay`, ADR-084's amendment of 2026-10-13), and how
 * long after its respawn tick a dead character its player never asked for stands up by itself (ADR-073's amendment "the
 * respawn is the player's", decision 5), and when a character is idle and AFK (ADR-090 decision 1): idle after `idle_after`
 * ticks without player-driven input or after `away_after` with its page away, and AFK after `afk_after`, and the
 * least ticks between two pops of one shrine, the pop strip's length (`shrine_pop`, ADR-096 decision 25), and the least
 * flight of a projectile, so a shot stopped short by a wall is still seen (`projectile_min_flight`, ADR-095 decision 5).
 * The death penalty's three numbers are counts, not ticks (`CONSTANT_COUNT_IDS`).
 * So is `discovery_xp`, the XP a character earns for its first discovery of a shrine (ADR-096's amendment of
 * 2026-10-05): a discovery is the first use of that source, and other discoveries join it with the systems that make
 * them. And so are the three XP rates, in whole percents (ADR-083's amendment of 2026-10-07, A1): `xp_percent_protected`,
 * `xp_percent_flagged` and `xp_percent_hostile`, a creature kill's XP multiplier for a Protected character, a Flagged or
 * Combatant one and anyone under a Hostile zone's lock. And so is `unpaid_blow_damage`, the whole percents of a paid hit that a basic blow swung unpaid deals (ADR-117
 * decision 6). And so are the two speeds, in speed units, 16 to a sixteenth (ADR-116 decisions 25, 30 and 31):
 * `walk_speed`, what a walking character's steps average, and `creature_speed`, the length of a creature's step, a
 * whole number of sixteenths. And so are the run's (ADR-116 decisions 2 and 5): `run_speed`, in speed units,
 * `run_drain`, the Stamina points a second of running takes, and `run_restart`, the Stamina a character that ran itself
 * empty must hold before it runs again. `jump_ticks` is ticks: the least between two jumps of one character
 * (decision 17). And so is `creature_opening_throws`, how many throws a creature that holds a projectile makes from
 * where it stands at the start of a chase before it only rushes, 0 for none (ADR-095's amendment of 2026-10-07, A6).
 * And so is `companion_fly_gap`, the tiles of gap past which a player's companion takes to the air whatever his player does
 * (ADR-142 decision 7.2, P2.26-T6), read by the page that draws him, whose rules version it moves. And so is `pvp_level`, the level a character must reach before it may choose to flag, 1 to `MAX_PVP_LEVEL` (ADR-076's
 * amendment of 2026-10-08, C1). And so is `xp_above_cap`, how many levels above a party mate who dealt no damage a
 * creature's level counts for the XP of its kill, 0 to `MAX_XP_ABOVE_CAP` (ADR-083's note of 2026-10-15 and its amendment
 * of 2026-10-21; a character that dealt damage is paid at the creature's real level). And so are the party share's four numbers
 * (ADR-083's note of 2026-10-16): `party_share_percent`, `party_bonus_percent` and `party_bonus_max_percent`, 0 to 100, and
 * `party_share_tiles`, 1 to 255. And so are the travel fare's seven numbers (ADR-137 decisions 19 and 20 and their
 * amendment of 2026-10-28): `travel_coin_base`, `travel_coin_per_tile` and `travel_coin_cap`, copper from 0 to
 * `MAX_TRAVEL_COPPER`, and `travel_focus_percent`, `travel_anywhere_focus_percent`, `travel_anywhere_focus_per_10`
 * and `travel_anywhere_focus_cap`, 0 to 100. And so are the attributes' (ADR-138 decisions 2, 5 and 8): `attribute_start`
 * (1 to 100), `attribute_cap` (1 to 999, at least the start) and `attribute_points_per_level` (0 to 10), and the combat
 * rates `attribute_<attribute>_<effect>`, each in basis points of the thing it changes for one point from the start, a
 * `_max` the most that effect moves either way and `attribute_perception_crit_floor` the most the critical chance
 * falls, all in basis points, 0 to 10,000.
 */
export const CONSTANT_IDS = ["in_combat", "respawn_delay", "combatant", "flag_on", "flag_off", "flag_on_safe", "flag_off_safe", "diminishing_window", "linger", "logout_stay", "respawn_auto", "idle_after", "afk_after", "away_after", "shrine_pop", "death_gap_levels", "death_stacks_elite", "death_stacks_boss", "projectile_min_flight", "discovery_xp", "xp_percent_protected", "xp_percent_flagged", "xp_percent_hostile", "unpaid_blow_damage", "walk_speed", "creature_speed", "run_speed", "run_drain", "run_restart", "jump_ticks", "creature_opening_throws", "pvp_level", "xp_above_cap", "party_share_percent", "party_bonus_percent", "party_bonus_max_percent", "party_share_tiles", "travel_coin_base", "travel_coin_per_tile", "travel_coin_cap", "travel_focus_percent", "travel_anywhere_focus_percent", "travel_anywhere_focus_per_10", "travel_anywhere_focus_cap", "attribute_start", "attribute_cap", "attribute_points_per_level", "attribute_vitality_health", "attribute_vitality_taken", "attribute_vitality_taken_max", "attribute_might_damage", "attribute_agility_speed", "attribute_agility_speed_max", "attribute_agility_cooldown", "attribute_agility_cooldown_max", "attribute_agility_dodge", "attribute_agility_dodge_max", "attribute_willpower_focus", "attribute_willpower_regeneration", "attribute_willpower_control", "attribute_willpower_control_max", "attribute_perception_crit", "attribute_perception_crit_floor", "attribute_perception_crit_max", "attribute_intellect_damage", "companion_fly_gap"] as const;
export type ConstantId = (typeof CONSTANT_IDS)[number];

/**
 * The constants that count something other than ticks, each held as a `count`: the death penalty's weight (ADR-079's
 * amendment of 2026-10-04, item 2), the levels a killer must stand above its victim to bring one more stack, and the
 * stacks more an elite and a boss bring; the XP of a discovery, held to `MAX_DISCOVERY_XP`; the three XP rates, each from
 * `MIN_XP_PERCENT` to `MAX_XP_PERCENT` and in order (`checkXpRates`); and the unpaid blow's
 * share of a paid hit, 1 to `MAX_UNPAID_BLOW_PERCENT`; the walk's and a creature's speed, `MIN_SPEED_UNITS` to
 * `MAX_SPEED_UNITS`; and the run's speed, from `MIN_SPEED_UNITS`, its drain, 1 to `MAX_RUN_DRAIN`, and its restart;
 * and a creature's opening throws, 0 to `MAX_OPENING_THROWS`; and the XP cap above a party mate who dealt no damage, 0 to `MAX_XP_ABOVE_CAP`.
 */
export const CONSTANT_COUNT_IDS = ["death_gap_levels", "death_stacks_elite", "death_stacks_boss", "discovery_xp", "xp_percent_protected", "xp_percent_flagged", "xp_percent_hostile", "unpaid_blow_damage", "walk_speed", "creature_speed", "run_speed", "run_drain", "run_restart", "creature_opening_throws", "pvp_level", "xp_above_cap", "party_share_percent", "party_bonus_percent", "party_bonus_max_percent", "party_share_tiles", "travel_coin_base", "travel_coin_per_tile", "travel_coin_cap", "travel_focus_percent", "travel_anywhere_focus_percent", "travel_anywhere_focus_per_10", "travel_anywhere_focus_cap", "attribute_start", "attribute_cap", "attribute_points_per_level", "attribute_vitality_health", "attribute_vitality_taken", "attribute_vitality_taken_max", "attribute_might_damage", "attribute_agility_speed", "attribute_agility_speed_max", "attribute_agility_cooldown", "attribute_agility_cooldown_max", "attribute_agility_dodge", "attribute_agility_dodge_max", "attribute_willpower_focus", "attribute_willpower_regeneration", "attribute_willpower_control", "attribute_willpower_control_max", "attribute_perception_crit", "attribute_perception_crit_floor", "attribute_perception_crit_max", "attribute_intellect_damage", "companion_fly_gap"] as const satisfies readonly ConstantId[];
export type CountConstantId = (typeof CONSTANT_COUNT_IDS)[number];
export type TickConstantId = Exclude<ConstantId, CountConstantId>;

/**
 * The most XP `discovery_xp` may hold, so `progress` carries a grant in its `u16` and `shrines` in its `xp`.
 */
export const MAX_DISCOVERY_XP = 65_535;

/**
 * The least an XP rate may hold, in whole percents: the full amount, so no flag state earns less than it (ADR-083's
 * amendment of 2026-10-07, A1; R-0179).
 */
export const MIN_XP_PERCENT = 100;

/**
 * The most an XP rate may hold, in whole percents: a bound that refuses a slip such as 1250 for 125 (ADR-083's
 * amendment of 2026-10-07, A1).
 */
export const MAX_XP_PERCENT = 1_000;

/**
 * The most `unpaid_blow_damage` may hold: the whole of the paid hit, so an unpaid blow never deals more than a paid one.
 */
export const MAX_UNPAID_BLOW_PERCENT = 100;

/**
 * The least a speed constant may hold, in speed units: one whole sixteenth a step, so every step moves.
 */
export const MIN_SPEED_UNITS = 16;

/**
 * The most a speed constant may hold, in speed units: six sixteenths a step, the range the step pattern is proved over
 * (`movement/step.test.ts`).
 */
export const MAX_SPEED_UNITS = 96;

/**
 * The most `run_drain` may hold, in Stamina points a second of running.
 */
export const MAX_RUN_DRAIN = 1000;

/**
 * The most `pvp_level` may hold: the level is a byte (ADR-076's amendment of 2026-10-08, C1).
 */
export const MAX_PVP_LEVEL = 255;

/**
 * The most `xp_above_cap` may hold: a level is a byte.
 */
export const MAX_XP_ABOVE_CAP = 255;

/**
 * The most copper a travel fare constant may hold: a million, a hundred Sovereigns, far past any fare the game means to
 * charge, so a slipped digit is refused while the capped fare stays well inside `CURRENCY_MAX`.
 */
export const MAX_TRAVEL_COPPER = 1_000_000;

/**
 * The most `creature_opening_throws` may hold: more throws than any opening has a use for, and well under the byte a
 * creature counts them in.
 */
export const MAX_OPENING_THROWS = 8;

/**
 * The most ticks a resource's `regeneration.delayTicks` may hold (ADR-117 decision 2).
 */
export const MAX_REGENERATION_DELAY_TICKS = 65_535;

/**
 * The crowd-control categories an effect may belong to (Bible 5.5, ADR-079 decisions 1 and 4): what it forbids and the
 * diminishing-returns track it counts on. Root, silence, slow, blind and disarm join with the abilities that apply them.
 */
export const CONTROL_KINDS = ["stun"] as const;
export type ControlKind = (typeof CONTROL_KINDS)[number];

/**
 * What a second application of an effect does (ADR-079 decision 3 and its amendment of 2026-10-02): `refresh` renews
 * its time, `stack` adds a stack, and `intensity` renews its time and keeps the greater of the stacks held and the
 * stacks the application brings, so an effect's stacks are its strength and two applications never add up.
 */
export const STACKING_RULES = ["refresh", "stack", "intensity"] as const;
export type StackingRule = (typeof STACKING_RULES)[number];

/**
 * The causes of a death a penalty may follow (Bible 6.3, ADR-079 decision 7): a creature or nothing, a character, or
 * a character in a Hostile zone.
 */
export const DEATH_CAUSES = ["pve", "pvp", "hostile"] as const;
export type DeathCause = (typeof DEATH_CAUSES)[number];

/**
 * The longest an effect lasts, so the owner's `effects` frame carries its time left in a `u16` (ADR-079 decision 9).
 */
export const MAX_EFFECT_TICKS = 65_535;

/**
 * The crits the game rolls (Bible 5.4): against a creature, and against a character.
 */
export const CRIT_IDS = ["pve", "pvp"] as const;
export type CritId = (typeof CRIT_IDS)[number];

/**
 * The longest cooldown an ability may hold, so a `used` event carries it in a `u16` (ADR-073 decision 16).
 */
export const MAX_COOLDOWN_TICKS = 65_535;

/**
 * A certainty in basis points: a crit's chance runs from 0 to this.
 */
export const BASIS_POINTS = 10_000;

/**
 * The largest number a table may hold, the largest a Postgres `integer` holds.
 */
export const MAX_RULES_NUMBER = 2_147_483_647;

/**
 * An id: 1 to 32 characters of `a-z`, `0-9` and `_`, starting with a letter.
 */
export const RULES_ID_PATTERN = /^[a-z][a-z0-9_]{0,31}$/;

/**
 * The longest name a player reads, in characters.
 */
export const MAX_RULES_NAME_CHARS = 32;

/**
 * What an ability spends of a resource when it resolves.
 */
export interface Cost {
  readonly resource: ResourceId;
  readonly amount: number;
}

/**
 * A cone from the caster's feet along the aim: its reach and its whole angle, centred on the aim, the most targets it
 * strikes, nearest first (ADR-073 decision 7), and the ticks from the act to its blow, `impactTicks`: 0 lands in the
 * act's tick, more waits for the strip's impact, as a creature's blow does (ADR-095's amendment of 2026-10-10).
 */
export interface ConeShape {
  readonly kind: "cone";
  readonly radiusSixteenths: number;
  readonly degrees: number;
  readonly maxTargets: number;
  readonly impactTicks: number;
}

/**
 * A line from the caster's feet along the aim, stopping at the first solid cell (Bible 13.6), and the most targets it
 * strikes, nearest first.
 */
export interface LineShape {
  readonly kind: "line";
  readonly lengthSixteenths: number;
  readonly widthSixteenths: number;
  readonly maxTargets: number;
}

/**
 * The selected target, within a range.
 */
export interface TargetShape {
  readonly kind: "target";
  readonly rangeSixteenths: number;
}

/**
 * Oneself, or a selected ally within a range (ADR-079's amendment of 2026-10-03): an act naming no character lands on
 * its caster, one naming another living character on that character once it is in view, range and sight.
 */
export interface AllyShape {
  readonly kind: "ally";
  readonly rangeSixteenths: number;
}

/**
 * Its caster alone (ADR-091 decision 2): no target, no aim and no measure.
 */
export interface SelfShape {
  readonly kind: "self";
}

/**
 * A shot (ADR-095 decision 2): it leaves the caster `releaseTicks` after the act, flies at `speedSixteenths` a tick to a
 * landing point at most `rangeSixteenths` from the caster's origin and bursts there in a circle of `radiusSixteenths`,
 * striking at most `maxTargets`, nearest the landing point first. A creature does not choose it for a target nearer than
 * `minRangeSixteenths`. `look` names the projectile look the page draws.
 */
export interface ProjectileShape {
  readonly kind: "projectile";
  readonly rangeSixteenths: number;
  readonly minRangeSixteenths: number;
  readonly speedSixteenths: number;
  readonly radiusSixteenths: number;
  readonly maxTargets: number;
  readonly releaseTicks: number;
  readonly look: string;
}

/**
 * Where an ability reaches.
 */
export type AbilityShape = ConeShape | LineShape | TargetShape | AllyShape | SelfShape | ProjectileShape;

/**
 * The most health a heal restores at once, so a `heal` event carries it in a `u16` (ADR-091 decisions 2 and 11).
 */
export const MAX_HEAL_AMOUNT = 65_535;

/**
 * What an ability aimed at oneself or an ally restores at once (ADR-091 decision 2): whole points of health, never past
 * the bearer's maximum.
 */
export interface Heal {
  readonly amount: number;
}

/**
 * What an ability aimed at oneself or an ally restores of a pool other than health at once (ADR-091's note for
 * P2.8-T57): whole points, never past the bearer's maximum.
 */
export interface Restore {
  readonly amount: number;
}

/**
 * The variants of a drink (ADR-074's amendment of 2026-10-27), each one animation state and one tone of the burst.
 */
export type DrinkVariant = "health" | "stamina" | "cure";

/**
 * What marks an ability as a potion's drink: its variant, and the burst's size for the drinker's own page as a whole
 * percent of the definition's (60 minor, 100 standard, above 100 stronger).
 */
export interface Drink {
  readonly variant: DrinkVariant;
  readonly percent: number;
}

/**
 * An effect whose time left an ability aimed at oneself or an ally cuts (ADR-091's note for P2.8-T57, item 3): the
 * effect's id and the whole percent of the ticks left it takes off, rounded down.
 */
export interface Shorten {
  readonly effect: string;
  readonly percent: number;
}

/**
 * The damage an ability deals before crits, levels and effects.
 */
export interface Damage {
  readonly amount: number;
  readonly type: DamageType;
}

/**
 * How an ability moves its caster (ADR-073's amendment of 2026-10-02, item 3): along its line's length over a number
 * of ticks, as Evasion's dash does.
 */
export interface Displacement {
  readonly ticks: number;
}

/**
 * How a hit of one class lands an effect (ADR-079's amendment of 2026-10-02, item 3): the stacks it brings and the range
 * of ticks its duration is drawn from, both ends included.
 */
export interface HitLanding {
  readonly stacks: number;
  readonly minTicks: number;
  readonly maxTicks: number;
}

/**
 * The hit classes an `OnHit` lands by (item 4): `light` when mitigation or shields took anything off the hit, else
 * `critical` when it critted, else `normal`.
 */
export const HIT_CLASSES = ["light", "normal", "critical"] as const;
export type HitClass = (typeof HIT_CLASSES)[number];

/**
 * An effect an ability's hit may land on its target (ADR-079's amendment of 2026-10-02, item 3): the effect's id, the
 * chance in basis points, rolled once a hit, and how each hit class lands it.
 */
export interface OnHit {
  readonly effect: string;
  readonly chanceBasisPoints: number;
  readonly light: HitLanding;
  readonly normal: HitLanding;
  readonly critical: HitLanding;
}

/**
 * The strips consecutive uses of an ability play in turn (ADR-073's amendment "the Strike combo and the Heavy Strike
 * chain", item 2): 2 to 4 action strips, the first the ability's `strip`, and the ticks past the last use's ready tick
 * within which the next use still plays the following strip.
 */
export interface Combo {
  readonly strips: readonly string[];
  readonly graceTicks: number;
}

/**
 * One blow of a chain: the ticks after the act it resolves at, the strip it plays and the damage it deals, of the
 * ability's damage type.
 */
export interface ChainHit {
  readonly atTicks: number;
  readonly strip: string;
  readonly amount: number;
}

/**
 * An ability that strikes more than once from one act (the amendment's item 4): its blows in order, the first at 0,
 * and the ticks after the act during which its caster neither moves nor starts another ability.
 */
export interface Chain {
  readonly hits: readonly ChainHit[];
  readonly rootTicks: number;
}

/**
 * An effect an ability applies: an id, for the effect's own duration, or a record naming the ticks it lasts for this ability.
 */
export type AbilityEffect = string | { readonly effect: string; readonly durationTicks: number };

/**
 * An ability: its name and the character sheet animation it plays (ADR-036), its cooldown from a use until it is
 * ready again, what it costs, where it reaches, what it deals (null for none; a chain's total), the effects it applies
 * by id on every hit (a chain's on its last), each an id (the effect's own duration) or `{ effect, durationTicks }` (the ticks this ability names; ADR-079's amendment of 2026-10-21), whether every character is given it whatever its weapon, how it moves its
 * caster (null for not at all), the effects its hits may land on a roll (`onHit`), the effects it ends on each target its
 * hits land on (`cleanses`), the strips its consecutive uses alternate (`combo`, null for none), its blows when one
 * act strikes more than once (`chain`, null for one), the health it restores on the one it lands on (`heal`, null for
 * none; ADR-091 decision 2), the stamina it restores there (`restoreStamina`, null for none) and the effects whose time
 * left it cuts there (`shortens`; both ADR-091's note for P2.8-T57), and whether it is a potion's drink (`drink`, null for
 * not; ADR-074's amendment of 2026-10-27). `icon` is the key of the colour art a page draws it with (null for none; ADR-111
 * decision 1).
 */
export interface Ability {
  readonly id: string;
  readonly name: string;
  readonly strip: string;
  readonly cooldownTicks: number;
  readonly costs: readonly Cost[];
  readonly shape: AbilityShape;
  readonly damage: Damage | null;
  readonly effects: readonly AbilityEffect[];
  readonly given: boolean;
  readonly displacement: Displacement | null;
  readonly onHit: readonly OnHit[];
  readonly cleanses: readonly string[];
  readonly combo: Combo | null;
  readonly chain: Chain | null;
  readonly heal: Heal | null;
  readonly restoreStamina: Restore | null;
  readonly shortens: readonly Shorten[];
  readonly drink: Drink | null;
  readonly icon: string | null;
}

/**
 * One of a weapon's blows and the ability it performs.
 */
export interface WeaponBlow {
  readonly blow: Blow;
  readonly ability: string;
}

/**
 * A weapon: its name, whether it is the one every character is given, and its blows, `basic` always.
 */
export interface Weapon {
  readonly id: string;
  readonly name: string;
  readonly starting: boolean;
  readonly blows: readonly WeaponBlow[];
}

/**
 * What an effect changes, per stack, in whole percents but `absorb` (ADR-079 decision 1): damage its bearer deals and
 * takes, its maximum health, its cooldowns, and the points of damage its shield absorbs. `focusRegeneration`, optional
 * (ADR-137's amendment of 2026-10-28), is added to its bearer's Focus regeneration, -100 to 1000 and never 0 when
 * written; a record without it leaves regeneration alone. `attributes`, optional (ADR-138 decision 7), is the flat
 * points each stack adds to the attributes it names, -10 to 10 and never 0. `moveSpeed`, optional (ADR-144's amendment of
 * 2026-11-01), is added to its bearer's walk and run speed in whole percents, -50 to 0 and never 0 when written.
 */
export interface EffectModifiers {
  readonly damageDealt: number;
  readonly damageTaken: number;
  readonly maxHealth: number;
  readonly cooldown: number;
  readonly absorb: number;
  readonly focusRegeneration?: number;
  readonly moveSpeed?: number;
  readonly attributes?: Readonly<Partial<Record<Attribute, number>>>;
}

/**
 * What a periodic effect does (ADR-079's amendment of 2026-10-02, item 1): every `everyTicks` its bearer takes
 * `amount` damage of `type` per stack, and a death it causes is named by `cause`.
 */
export interface Periodic {
  readonly everyTicks: number;
  readonly amount: number;
  readonly type: DamageType;
  readonly cause: string;
}

/**
 * An effect (ADR-079 decision 1): its name and how long one application lasts, whether watchers see it, the
 * crowd-control category it belongs to (null for none), what a second application does and the most stacks it holds,
 * whether it survives its bearer's death, the death cause it is the penalty for (null for none), its modifiers, and
 * the damage it deals over time (null for none). `icon` is the key of the colour art a page draws it with (null for
 * none; ADR-111 decision 1). `tip`, optional, names the line of `bar.effectTips` its tooltip adds (ADR-144's amendment of
 * 2026-11-01).
 */
export interface Effect {
  readonly id: string;
  readonly name: string;
  readonly durationTicks: number;
  readonly visible: boolean;
  readonly control: ControlKind | null;
  readonly stacking: StackingRule;
  readonly maxStacks: number;
  readonly persistsDeath: boolean;
  readonly onDeath: DeathCause | null;
  readonly modifiers: EffectModifiers;
  readonly periodic: Periodic | null;
  readonly icon: string | null;
  readonly tip?: string;
}

/**
 * The slots of the bar (ADR-121 decision 2.1): one bar of ten, keyed "Ability 1" to "Ability 10". A player's bar
 * record holds exactly this many, and the default bar must fit in them (`checkBarSize`).
 */
export const BAR_SLOTS = 10;

/**
 * The slots of the tool belt (ADR-142 decision 3b and its note of 2026-10-29): four, keyed "Tool 1" to "Tool 4". A
 * player's belt record holds exactly this many.
 */
export const TOOL_BELT_SLOTS = 4;

/**
 * What an item is (ADR-091 decision 1). Only a consumable does anything yet; the other kinds validate, stack and show
 * in the bag, so the row that gives one behaviour needs no migration.
 */
export const ITEM_KINDS = ["consumable", "equipment", "material", "quest"] as const;
export type ItemKind = (typeof ITEM_KINDS)[number];

/**
 * What an item is for beyond its kind, a closed set (ADR-142 decision 3a): `worm`, the Aethling's treat, and `bait`,
 * what a rod will take once fishing exists. Nothing reads `bait` yet; an item holds each tag at most once.
 */
export const ITEM_TAGS = ["worm", "bait"] as const;
export type ItemTag = (typeof ITEM_TAGS)[number];

/**
 * The kinds of tool an equipment item may be (ADR-142 decision 3a), the closed set a harvest group names as the tool
 * it needs: a sickle cuts tall grass. Later gathering adds an axe and a pick.
 */
export const TOOL_KINDS = ["sickle"] as const;
export type ToolKind = (typeof TOOL_KINDS)[number];

/**
 * An item's rarity (ADR-125 decision 1.1, Bible 10.5): the ladder from most common to rarest, Common, Uncommon,
 * Rare, Epic and Legendary, then Relic, which stands apart from the ladder. Every item has one. Only the page's slot,
 * the item card, the chat link and the Rarity sort read it; no rule does.
 */
export const ITEM_RARITIES = ["common", "uncommon", "rare", "epic", "legendary", "relic"] as const;
export type ItemRarity = (typeof ITEM_RARITIES)[number];

/**
 * The largest stack an item may hold, the bound `character_items.quantity` checks.
 */
export const MAX_ITEM_STACK = 999;

/**
 * The longest item id, in characters: the bound `character_items.item` checks.
 */
export const MAX_ITEM_ID_CHARS = 48;

/**
 * An item's id: 1 to `MAX_ITEM_ID_CHARS` characters of `a-z`, `0-9` and `_`. It is what Postgres and the ledger store,
 * so it is never renamed or reused.
 */
export const ITEM_ID_PATTERN = new RegExp(`^[a-z0-9_]{1,${MAX_ITEM_ID_CHARS}}$`);

/**
 * The longest item name and item description, in characters.
 */
export const MAX_ITEM_NAME_CHARS = 48;
export const MAX_ITEM_DESCRIPTION_CHARS = 160;

/**
 * A glyph's name as the page's glyphs are named: 1 to 32 characters of `a-z`, `0-9` and `-`, starting with a letter.
 */
export const GLYPH_NAME_PATTERN = /^[a-z][a-z0-9-]{0,31}$/;

/**
 * An item (ADR-091 decision 1): its id, name, kind and rarity (ADR-125 decision 1.1), the most one bag slot holds of it, the ability record its use
 * runs (a consumable's only, else null), the sound set under `sfx/items/` it plays (null for silence), the glyph the
 * page draws it with (null for none), the key of the colour art a page draws in the glyph's place when it holds the art
 * (`icon`, null for none; ADR-111 decision 1) and the sentence its card reads, which may name tokens its own stats supply
 * (`itemDescription` fills them; ADR-091's amendment of 2026-10-09). `tags` (ADR-142 decision 3a) is present only on an
 * item with at least one tag, and `tool` only on an equipment item that is a tool.
 */
export interface Item {
  readonly id: string;
  readonly name: string;
  readonly kind: ItemKind;
  readonly rarity: ItemRarity;
  readonly stack: number;
  readonly use: string | null;
  readonly sound: string | null;
  readonly glyph: string | null;
  readonly icon: string | null;
  readonly description: string;
  readonly tags?: readonly ItemTag[];
  readonly tool?: ToolKind;
}

/**
 * The most of each resource a character holds.
 */
export type Pools = Readonly<Record<ResourceId, number>>;

/**
 * The most a level's `damagePercent` may be: a hundred times an ability's damage.
 */
export const MAX_DAMAGE_PERCENT = 10_000;

/**
 * A level (ADR-083 decision 1): its number, the total XP that reaches it, the whole percents of every ability's base
 * damage its blows deal, and its pools. The table's last row is the cap.
 */
export interface Level {
  readonly level: number;
  readonly xp: number;
  readonly damagePercent: number;
  readonly pools: Pools;
}

/**
 * A creature level (ADR-082's amendment of 2026-10-15, item 2): the creature's maximum health and the XP of a kill at
 * that level, each a whole percent of the same template's value at level 1, which every template holds. The table's last
 * row is the cap, equal to the player table's.
 */
export interface CreatureLevel {
  readonly level: number;
  readonly healthPercent: number;
  readonly xpPercent: number;
}

/**
 * A row of the XP level-gap table (ADR-083's note of 2026-10-15 and the author's roll-off): from `minGap` levels of the
 * earner above the creature, a kill pays `percent` of its XP, until the next row's `minGap`.
 */
export interface XpGapRow {
  readonly minGap: number;
  readonly percent: number;
}

/**
 * A rate of regeneration: `amount` points, or percents of the pool, every `everyTicks` ticks.
 */
export interface RegenerationRate {
  readonly amount: number;
  readonly unit: RegenerationUnit;
  readonly everyTicks: number;
}

/**
 * A resource's regeneration in combat and out of it, and the pause a spend of it starts (ADR-117 decision 2):
 * `delayTicks` ticks after the tick of a spend in which nothing is regained, 1 to `MAX_REGENERATION_DELAY_TICKS`, or
 * null for a resource no spend pauses.
 */
export interface Resource {
  readonly id: ResourceId;
  readonly regeneration: { readonly inCombat: RegenerationRate; readonly outOfCombat: RegenerationRate; readonly delayTicks: number | null };
}

/**
 * A constant the code reads by its id: a whole number of ticks, or, for an id of `CONSTANT_COUNT_IDS`, a whole count;
 * each id pairs with its own field alone.
 */
export type Constant = { readonly id: TickConstantId; readonly ticks: number } | { readonly id: CountConstantId; readonly count: number };

/**
 * A crit: its chance a hit in basis points, and the damage it deals in whole percents of the hit's.
 */
export interface Crit {
  readonly id: CritId;
  readonly chanceBasisPoints: number;
  readonly damagePercent: number;
}

/**
 * The twelve tables as the validator hands them back: records of the declared types, built from checked fields.
 */
export interface RulesTables {
  readonly abilities: readonly Ability[];
  readonly backgrounds: readonly Background[];
  readonly constants: readonly Constant[];
  readonly creature_levels: readonly CreatureLevel[];
  readonly crits: readonly Crit[];
  readonly effects: readonly Effect[];
  readonly items: readonly Item[];
  readonly levels: readonly Level[];
  readonly looks: readonly LookPiece[];
  readonly resources: readonly Resource[];
  readonly weapons: readonly Weapon[];
  readonly xp_gap: readonly XpGapRow[];
}

/**
 * One thing wrong with a set of tables: the table (`rules` for the set as a whole), the record's id, its place as
 * `#n` when it has no valid id, or null for the table itself, and what is wrong, naming the field.
 */
export interface RulesProblem {
  readonly table: string;
  readonly id: string | null;
  readonly message: string;
}

/**
 * What the validator says of a set of tables: the tables, or every problem it found.
 */
export type RulesVerdict = { readonly ok: true; readonly tables: RulesTables } | { readonly ok: false; readonly problems: readonly RulesProblem[] };

/**
 * The rules a map server or a page plays by (ADR-069 decision 6): the tables frozen all the way down, their records
 * indexed by id, the constants, resources and crits by id, the looks with each piece's wire number, the weapon every
 * character is given, and the version that names the tables' content.
 */
export interface Rules {
  readonly version: string;
  readonly tables: RulesTables;
  readonly abilities: ReadonlyMap<string, Ability>;
  readonly items: ReadonlyMap<string, Item>;
  readonly weapons: ReadonlyMap<string, Weapon>;
  readonly effects: ReadonlyMap<string, Effect>;
  readonly resources: Readonly<Record<ResourceId, Resource>>;
  readonly constants: Readonly<Record<ConstantId, number>>;
  readonly crits: Readonly<Record<CritId, Crit>>;
  readonly looks: Looks;
  readonly startingWeapon: Weapon;
}
